import { skipToken } from "@reduxjs/toolkit/query/react";
import {
  getOutcomeNoun,
  isRevenueOutcomeDefinition,
} from "@repo/app-types/entities/outcome";
import {
  asPlaybookId,
  getOutcomeDefinitionFromPlaybook,
  type PlaybookCriterionNode,
  type PlaybookId,
  type PlaybookNode,
  type PlaybookSectionNode,
} from "@repo/app-types/entities/playbook";
import {
  asTeammateId,
  type TeammateId,
} from "@repo/app-types/entities/teammate";
import { isNotNullNotUndefined } from "@repo/app-types/helpers/utilities";
import { Size } from "@repo/ui/helpers/helpers";
import { useNow } from "@repo/ui/timestamps/timestampContext";
import memoize from "lodash-es/memoize.js";
import { useMemo, type FC } from "react";
import { useParams } from "react-router";
import {
  usePlaybookGlobalAverageScorePerCriteriaQuery,
  usePlaybookTeammatePlaybookCriteriaScoresQuery,
  usePlaybookTeammatePlaybookCriterionCoverageSecondsQuery,
  useTeammateHighImpactCriteriaQuery,
} from "../../api/entities/performancePlaybook.js";
import { usePlaybookByIdQuery } from "../../api/entities/playbook.js";
import { useAppSelector } from "../../hooks/hook.js";
import { useModalNavigation } from "../../hooks/useModalNavigation.js";
import { selectCompany } from "../company/companySlice.js";
import { buildTeamPerformancePageQueryArgs } from "../filters/buildFilterQueryArgs.js";
import type { FilterDefaults } from "../filters/filters.js";
import { useFilters } from "../filters/filters.js";
import { CriteriaOutcomeCorrelationImpactPill } from "./criteriaOutcomeCorrelationImpactPill.js";
import { CriterionAverageScore } from "./criterionAverageScore.js";
import { CriterionTimeBar } from "./criterionTimeBar.js";
import { renderGapBarWithTooltip } from "./gapBar.js";
import { GradingPassRatePill } from "./gradingPassRatePill.js";
import { PlaybookCriteriaTreeTable } from "./playbookCriteriaTreeTable.js";
import { usePlaybookCriterionCoverageHelpers } from "./playbookCriterionCoverageHelpers.js";
import { countTrendDays } from "./trendChart.js";
import { computeTrendDelta, TrendDeltaLabel } from "./trendDeltaLabel.js";

interface TrainingTeammatePagePlaybookProps {
  /** If not provided, will be read from URL params */
  playbookId?: PlaybookId;
  /** If not provided, will be read from URL params */
  teammateId?: TeammateId;
  /** Custom function to generate the criterion navigation path. Defaults to `criteria/${criterionId}` */
  getCriterionPath?: (criterionId: string) => string;
  /**
   * The filter defaults of the page embedding this panel, so it counts the
   * same population. Passed in rather than resolved here because the panel
   * appears on the teammate page, which applies the playbook's defaults, and
   * in an agent's Overview tab, which does not.
   */
  filterDefaults: FilterDefaults;
}

/**
 * Renders a playbook assessment table with grading scores for a specific teammate.
 * Can be used as a standalone page (reading from URL params) or embedded in other components (via props).
 *
 * Page accessible via https://<host>/performance/playbooks/<playbookId>/teammates/<teammateId>/playbook
 */
export const TrainingTeammatePagePlaybook: FC<
  TrainingTeammatePagePlaybookProps
> = ({
  playbookId: playbookIdProp,
  teammateId: teammateIdProp,
  getCriterionPath,
  filterDefaults,
}) => {
  const params = useParams();
  const playbookId =
    playbookIdProp ??
    (params.playbookId ? asPlaybookId(params.playbookId) : undefined);

  const teammateId =
    teammateIdProp ??
    (params.teammateId ? asTeammateId(params.teammateId) : undefined);
  if (!playbookId || !teammateId) {
    throw new Error("Missing param playbookId or teammateId.");
  }

  const isCriteriaCoverageEnabled =
    useAppSelector(selectCompany)?.settings.criteria_coverage_enabled ?? false;

  const now = useNow();

  // Use the filter values from the teammate detail page
  const [
    {
      dateRange,
      teammateGroupIds,
      outcome,
      customFields,
      playbookConditions,
      signalId,
      signalMetadataFields,
      totalRecordingDuration,
      dateBasis,
    },
  ] = useFilters(
    [
      "dateRange",
      "teammateGroupIds",
      "outcome",
      "customFields",
      "playbookConditions",
      "signalId",
      "signalMetadataFields",
      "totalRecordingDuration",
      "dateBasis",
    ],
    { playbookId, teammateId },
    filterDefaults,
  );

  const trendDays = countTrendDays(dateRange);

  const baseArgs =
    teammateGroupIds !== undefined
      ? buildTeamPerformancePageQueryArgs(now, {
          playbookId,
          dateRange,
          teammateGroupIds,
          outcome,
          customFields,
          playbookConditions,
          signalId,
          signalMetadataFields,
          totalRecordingDuration,
          dateBasis,
        })
      : undefined;

  const criteriaScoresArgs = baseArgs
    ? {
        playbookId,
        teammateId,
        scope: { ...baseArgs.scope, playbookId },
        filters: baseArgs.filters,
      }
    : skipToken;

  const criterionSlideoverNavigation = useModalNavigation();

  const { data: playbook } = usePlaybookByIdQuery(playbookId);

  const { data: playbookCriteriaScoresData, isLoading: isLoadingScores } =
    usePlaybookTeammatePlaybookCriteriaScoresQuery(criteriaScoresArgs);
  const playbookCriteriaScores = playbookCriteriaScoresData?.criteriaScores;

  // The query is skipped until the group filter loads from preferences, which
  // is still "no scores yet".
  const hasNoScoresYet = criteriaScoresArgs === skipToken || isLoadingScores;

  const outcomeDefinition = getOutcomeDefinitionFromPlaybook(playbook);

  const getTeammateNodeScore = useMemo(() => {
    if (!playbookCriteriaScores) {
      return () => undefined;
    }
    return memoize((node: PlaybookNode) => {
      const score = playbookCriteriaScores.find(
        (item) => item.criteria_node_id === node.id,
      );
      if (!score) {
        return undefined;
      }
      return {
        averageScore: score.average_score,
        averageScoreTrend: score.average_score_sparkline,
        scoreDistribution: score.score_distribution,
      };
    });
  }, [playbookCriteriaScores]);

  const { data: playbookCriterionCoverageSeconds } =
    usePlaybookTeammatePlaybookCriterionCoverageSecondsQuery(
      baseArgs
        ? {
            teammateId,
            scope: { ...baseArgs.scope, playbookId },
            filters: baseArgs.filters,
          }
        : skipToken,
    );

  const {
    hasAverageCoverageSeconds,
    getNodeAverageCoverageSeconds,
    getNodeTopPerformerAverageCoverageSeconds,
    maxAverageCoverageSeconds,
  } = usePlaybookCriterionCoverageHelpers(playbookCriterionCoverageSeconds);

  const { data: highImpactCriteria } = useTeammateHighImpactCriteriaQuery({
    playbookId,
    teammateId,
    asOfDateIso: dateRange.end?.toISO() ?? now.endOf("day").toISO(),
  });

  const highImpactDetailByCriteriaId = useMemo(() => {
    if (!highImpactCriteria) return undefined;
    return new Map(
      highImpactCriteria.high_impact_criteria
        .filter((entry) => entry.is_high_impact)
        .map((entry) => [entry.criteria_node_id, entry]),
    );
  }, [highImpactCriteria]);

  // Fetch company/group-level average and median scores for coaching priority
  // warnings.
  const { data: globalAverageScorePerCriteria } =
    usePlaybookGlobalAverageScorePerCriteriaQuery(
      baseArgs
        ? {
            scope: { ...baseArgs.scope, playbookId },
            filters: baseArgs.filters,
          }
        : skipToken,
    );

  const outcomeNoun = outcomeDefinition
    ? getOutcomeNoun(outcomeDefinition)
    : undefined;
  const isRevenueOutcome = outcomeDefinition
    ? isRevenueOutcomeDefinition(outcomeDefinition)
    : undefined;

  if (!playbook) {
    return (
      <>
        <div className="h-8 w-[70%]">
          <div className="h-full w-full animate-pulse rounded-full bg-gray-300" />
        </div>
      </>
    );
  }

  const renderTimeCell = (node: PlaybookNode) => {
    const teammateAverageSeconds = getNodeAverageCoverageSeconds(node);
    if (teammateAverageSeconds === undefined) return null;
    return (
      <CriterionTimeBar
        teammateAverageSeconds={teammateAverageSeconds}
        topPerformerAverageSeconds={getNodeTopPerformerAverageCoverageSeconds(
          node,
        )}
        maxSeconds={maxAverageCoverageSeconds ?? teammateAverageSeconds}
      />
    );
  };

  const renderCriterionPills = (criterion: PlaybookCriterionNode) => {
    const detail = highImpactDetailByCriteriaId?.get(criterion.id);
    if (!detail || !highImpactCriteria) {
      return null;
    }

    return (
      <CriteriaOutcomeCorrelationImpactPill
        isCollapsed={false}
        rank={detail.rank}
        totalCriteria={highImpactCriteria.total_criteria_count}
        netConversions={Math.round(detail.net_conversions)}
        averageRevenuePerOpp={
          highImpactCriteria.average_revenue_per_opp ?? undefined
        }
        outcomeNoun={outcomeNoun}
        outcomePluralNoun={outcomeDefinition?.plural_noun}
        isRevenueOutcome={isRevenueOutcome}
        isTeammateLevel
      />
    );
  };

  const renderCriterionScore = (criterion: PlaybookCriterionNode) => {
    const teammateScore = getTeammateNodeScore(criterion);
    const averageScore = teammateScore?.averageScore;
    const averageScoreTrend = teammateScore?.averageScoreTrend;
    const globalScore = globalAverageScorePerCriteria?.find(
      (item) => item.criteria_node_id === criterion.id,
    );
    const globalMedianScore = globalScore?.global_median_score;

    const gapBar = (() => {
      if (
        averageScore === undefined ||
        globalMedianScore === undefined ||
        globalMedianScore === null
      ) {
        return null;
      }
      // "yes_no" gradings are stored as rates. Let's use a [0, 100] range to display them.
      // "1_5_score" gradings should be displayed as is (with a [1, 5] range). Let's round them to the nearest decimal though.
      const isYesNoGradingValueType = criterion.grading_value_type === "yes_no";
      const score = isYesNoGradingValueType
        ? Math.round(averageScore * 100)
        : parseFloat(averageScore.toFixed(1));
      const benchmark = isYesNoGradingValueType
        ? Math.round(globalMedianScore * 100)
        : parseFloat(globalMedianScore.toFixed(1));
      const tooltipContentPrefix = isYesNoGradingValueType
        ? `${score}%`
        : `${score}`;
      const range: [number, number] = isYesNoGradingValueType
        ? [0, 100]
        : [1, 5];
      return renderGapBarWithTooltip(tooltipContentPrefix, score, benchmark, {
        range,
      });
    })();

    return (
      <div className="flex items-center gap-3">
        <CriterionAverageScore
          valueType={criterion.grading_value_type}
          value={averageScore}
          isLoading={hasNoScoresYet}
          sizedToMax
        />
        {gapBar}
        {isNotNullNotUndefined(averageScore) &&
        isNotNullNotUndefined(averageScoreTrend) ? (
          <TrendDeltaLabel
            delta={computeTrendDelta(averageScoreTrend)}
            gradingValueType={criterion.grading_value_type}
            trendDays={trendDays}
            size={Size.SMALL}
          />
        ) : null}
      </div>
    );
  };

  const renderSectionScore = (section: PlaybookSectionNode) => {
    const sectionScore = getTeammateNodeScore(section);
    const averageScore = sectionScore?.averageScore;
    const averageScoreTrend = sectionScore?.averageScoreTrend;
    const globalMedianScore = globalAverageScorePerCriteria?.find(
      (item) => item.criteria_node_id === section.id,
    )?.global_median_score;

    const gapBar = (() => {
      if (
        averageScore === undefined ||
        globalMedianScore === undefined ||
        globalMedianScore === null
      ) {
        return null;
      }
      // Section scores are pass rates (0-1), display as percentages (0-100)
      const score = Math.round(averageScore * 100);
      const benchmark = Math.round(globalMedianScore * 100);
      return renderGapBarWithTooltip(`${score}%`, score, benchmark, {
        range: [0, 100],
      });
    })();

    return (
      <div className="flex items-center gap-3">
        <GradingPassRatePill
          size={Size.SMALL}
          passRate={averageScore}
          isLoading={hasNoScoresYet}
          sizedToMax
        />
        {gapBar}
        {isNotNullNotUndefined(averageScore) &&
        isNotNullNotUndefined(averageScoreTrend) ? (
          <TrendDeltaLabel
            delta={computeTrendDelta(averageScoreTrend)}
            gradingValueType="yes_no"
            trendDays={trendDays}
            size={Size.SMALL}
          />
        ) : null}
      </div>
    );
  };

  return (
    <PlaybookCriteriaTreeTable
      assessmentTree={playbook.assessment_tree}
      showsTimeColumn={hasAverageCoverageSeconds && isCriteriaCoverageEnabled}
      renderSection={(section) => ({
        score: renderSectionScore(section),
        // Disabled for now: time: renderTimeCell(section)
      })}
      renderCriterion={(criterion) => {
        const defaultCriterionPath = `criteria/${encodeURIComponent(criterion.id)}`;
        return {
          titleSuffix: renderCriterionPills(criterion),
          score: renderCriterionScore(criterion),
          time: renderTimeCell(criterion),
          onPress: () =>
            criterionSlideoverNavigation.push(
              getCriterionPath?.(criterion.id) ?? defaultCriterionPath,
            ),
        };
      }}
    />
  );
};
